/**
 * Realtime channel for /api/events.
 *
 * Events say *what* changed, never the new value, so every handler here does
 * one thing: invalidate the queries that could have gone stale. A missed event
 * self-heals on the next refetch, which is why the reconnect can afford to back
 * off instead of hammering.
 *
 * The socket is a module singleton with a refcount rather than per-component
 * state: React 18 StrictMode mounts effects twice in development, and two live
 * sockets would double every invalidation.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { UNAUTHORIZED_EVENT, type KilnEvent } from './api';

export type ConnectionState = 'connecting' | 'open' | 'offline';

export interface SyncProgress {
  accountId: number;
  folder: string;
  done: number;
  total: number;
}

const BACKOFF_BASE_MS = 500;
const BACKOFF_FACTOR = 1.7;
const BACKOFF_MAX_MS = 30_000;
/** Sync ticks arrive per-folder-batch; refetching the list on each one thrashes. */
const PROGRESS_REFETCH_INTERVAL_MS = 4_000;

let socket: WebSocket | null = null;
let client: QueryClient | null = null;
let refs = 0;
let attempt = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let lastProgressRefetch = 0;
let stopped = true;

// --- Snapshot state exposed to React ---------------------------------------

let connection: ConnectionState = 'offline';
let progress: ReadonlyMap<number, SyncProgress> = new Map();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setConnection(next: ConnectionState): void {
  if (connection === next) return;
  connection = next;
  emit();
}

function setProgress(next: SyncProgress): void {
  const map = new Map(progress);
  // A finished folder should not leave a stale 100% bar pinned in the sidebar.
  if (next.total > 0 && next.done >= next.total) map.delete(next.accountId);
  else map.set(next.accountId, next);
  progress = map;
  emit();
}

// --- Connection -------------------------------------------------------------

function url(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/api/events`;
}

function scheduleReconnect(): void {
  if (stopped || retryTimer) return;
  const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * BACKOFF_FACTOR ** attempt);
  attempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    connect();
  }, delay);
}

function connect(): void {
  if (stopped || socket) return;
  setConnection('connecting');

  let ws: WebSocket;
  try {
    ws = new WebSocket(url());
  } catch {
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    attempt = 0;
    setConnection('open');
    // Whatever happened while the socket was down is unknown by definition;
    // one blanket refetch on reconnect is cheaper than trying to replay it.
    client?.invalidateQueries();
  };

  ws.onmessage = (ev: MessageEvent<string>) => {
    let event: KilnEvent;
    try {
      event = JSON.parse(ev.data) as KilnEvent;
    } catch {
      return;
    }
    handle(event);
  };

  ws.onclose = (ev) => {
    socket = null;
    setConnection('offline');
    // The server closes with 4401 when the cookie is gone; reconnecting would
    // just loop, so hand it to the shell and let it show the login screen.
    if (ev.code === 4401) {
      stopped = true;
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
      return;
    }
    scheduleReconnect();
  };

  ws.onerror = () => {
    // onclose always follows; closing here just makes the transition prompt.
    ws.close();
  };
}

function handle(event: KilnEvent): void {
  const qc = client;
  if (!qc) return;

  switch (event.type) {
    case 'hello':
      break;

    case 'thread-changed':
      qc.invalidateQueries({ queryKey: ['thread', event.threadKey] });
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      break;

    case 'threads-changed':
      for (const key of event.threadKeys) {
        qc.invalidateQueries({ queryKey: ['thread', key] });
      }
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      break;

    case 'account-status':
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      break;

    case 'sync-progress': {
      setProgress(event);
      const now = Date.now();
      if (now - lastProgressRefetch >= PROGRESS_REFETCH_INTERVAL_MS) {
        lastProgressRefetch = now;
        qc.invalidateQueries({ queryKey: ['threads'] });
      }
      break;
    }
  }
}

function wake(): void {
  if (stopped || socket) return;
  // A tab that just came back should not sit out the remaining backoff.
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  attempt = 0;
  connect();
}

function onVisibility(): void {
  if (document.visibilityState === 'visible') wake();
}

function start(qc: QueryClient): void {
  client = qc;
  refs += 1;
  if (refs > 1) return;
  stopped = false;
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', wake);
  connect();
}

function stop(): void {
  refs -= 1;
  if (refs > 0) return;
  stopped = true;
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('online', wake);
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (socket) {
    socket.onclose = null;
    socket.close();
    socket = null;
  }
  setConnection('offline');
}

// --- Hooks ------------------------------------------------------------------

/**
 * Opens the event stream and wires it to the query cache. Call once, at the
 * top of the authenticated tree.
 */
export function useKilnEvents(): ConnectionState {
  const qc = useQueryClient();
  const state = useSyncExternalStore(
    useCallback(
      (listener: () => void) => {
        start(qc);
        const unsubscribe = subscribe(listener);
        return () => {
          unsubscribe();
          stop();
        };
      },
      [qc],
    ),
    () => connection,
    () => 'offline' as const,
  );
  return state;
}

const EMPTY_PROGRESS: ReadonlyMap<number, SyncProgress> = new Map();

/** Live per-account sync progress, keyed by account id. Empty when idle. */
export function useSyncProgress(): ReadonlyMap<number, SyncProgress> {
  return useSyncExternalStore(
    subscribe,
    () => progress,
    () => EMPTY_PROGRESS,
  );
}

/** Connection state without opening a second subscription to the socket. */
export function useConnectionState(): ConnectionState {
  return useSyncExternalStore(
    subscribe,
    () => connection,
    () => 'offline' as const,
  );
}
